import { lazy, Suspense, useEffect, type ReactElement } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router-dom";
import { useUiStore } from "./uiStore.js";
import {
  CallbackPage,
  LoginChrome,
  LoginPage,
  RequireAuth,
  RequireRole,
  RoleContextProvider,
  TenantGate,
  isPopoutWindow,
} from "@aeriosiq/auth-client";
import {
  AnalysisPage,
  DataAcquisitionsPage,
  DefectCollectionsPage,
} from "@aeriosiq/lyse";
import { AppChrome } from "./chrome/AppChrome.js";
import { ComingSoonPage } from "./pages/ComingSoonPage.js";
// WS-NAV — workspace overview + the phone capability gate.
import { HomePage } from "./pages/HomePage.js";
import { DeviceGate } from "./pages/DesktopOnlyPage.js";
import { useIsMobileViewport } from "./workspaces/useWorkspaces.js";
import { DashboardPage } from "../features/dashboard/index.js";
import { SettingsPage } from "../features/account/index.js";
import { ComponentModelsPage } from "../features/component-models/index.js";
import { ComponentsPage } from "../features/components/index.js";
import {
  ProjectsPage,
  ProjectDetailPage,
  CreateProjectPage,
} from "../features/projects/index.js";
import { ContractsPage } from "../features/contracts/index.js";
import { ContractPositionsPage } from "../features/contract-positions/index.js";
import { InspectionInstructionsPage } from "../features/inspection-instructions/index.js";
// TT-02 — Field Data → Standby & Time Tracking (mobile-first, device: all).
// TT-03 — the back-office review table + the per-project commercial config.
import {
  MyTimeTrackingsPage,
  ProjectSettingsPage,
  TimeTrackingReviewPage,
} from "../features/time-tracking/index.js";
import { SitesPage } from "../features/sites/index.js";
import { TurbineModelsPage } from "../features/turbine-models/index.js";
import { TurbinesPage } from "../features/turbines/index.js";
import {
  AuditLogPage,
  ForbiddenPage,
  UsersListPage,
} from "../features/admin/index.js";
// CM-5/CM-6 — Administration → Contacts address book. Reachable by every
// role except pilot (contact.read), so it sits OUTSIDE the tenant_admin
// RequireRole subtree under its own non-pilot role gate.
import {
  ContactsListPage,
  ContactDetailPage,
} from "../features/admin/contacts/index.js";
// TNCY-5 — Administration → Partner Companies (cross-tenant links).
import {
  PartnerCompaniesPage,
  TenantSettingsPage,
} from "../features/tenancy/index.js";
// SB5-A — public /invite/:code page (no RequireAuth gate).
import { AcceptInvitePage } from "../features/invite/index.js";
// SB2-W-01 — cross-tenant Users page for platform staff.
import { CrossTenantUserListPage } from "../features/platform-admin/users/index.js";
// SUP-ADMIN-UI-01 — platform-admin bug-report triage surface.
import { BugReportTriagePage } from "../features/support/index.js";
// DEFCAT P6 — tenant-admin Defect Catalogue editor.
import {
  CatalogueEditorPage,
  CatalogueListPage,
} from "../features/defect-catalogues/index.js";
// AIF-01 — AI-Finding Model Management (list + detail).
import {
  AiModelsListPage,
  AiModelDetailPage,
} from "../features/ai-models/index.js";
// AIF-03 — Import AI Findings wizard.
import { ImportAiFindingsPage } from "../features/ai-findings-import/index.js";
// DI-2-1 — Field Data → Upload, the intake wizard. Lazy: the page and its Web
// Worker are chunks of their own and must not land in the shell bundle.
const IntakeUploadPage = lazy(() =>
  import("../features/intake/index.js").then((m) => ({
    default: m.IntakeUploadPage,
  })),
);
// DI-2-9 / DI-2-15 — Field Data → My intake: the intakes as a data page (the
// server's list + the sessions this browser has not created yet).
const IntakesPage = lazy(() =>
  import("../features/intake/index.js").then((m) => ({
    default: m.IntakesPage,
  })),
);
// DI-3-D — Field Data → Intake history: the finished data sets (board 5).
const IntakeHistoryPage = lazy(() =>
  import("../features/intake/index.js").then((m) => ({
    default: m.IntakeHistoryPage,
  })),
);
// DI-3-R2 — Field Data → My tasks: the pilot's to-do list (the open sets
// that need the pilot, derived from My Intake's reads).
const TasksPage = lazy(() =>
  import("../features/intake/index.js").then((m) => ({
    default: m.TasksPage,
  })),
);

/**
 * SB6-ANA-HEADER-01 — wrapper around AnalysisPage that collapses the
 * sidebar on route enter. Christian: "whenever we open a
 * DataAcquisition, we collapse the sidebar. we do not need to save
 * anything." Effect runs once per mount; we explicitly set
 * `sidebarCollapsed: true` rather than calling `toggleSidebar()` so a
 * second mount (same DA opened in a new tab from the same browser
 * profile, where it was already collapsed from a previous visit)
 * doesn't accidentally re-expand it. Persistence still lives in the
 * store — the user can re-open the sidebar via the toggle pill and it
 * stays open for the rest of the session.
 *
 * No restore-on-leave: per the brief "no persistence — pure side effect
 * on route enter". The sidebar stays in whatever state the user left it.
 */
function AnalysisPageWithSidebarCollapse(): ReactElement {
  useEffect(() => {
    useUiStore.setState({ sidebarCollapsed: true });
    // Intentionally empty cleanup — sidebar state is not reverted on
    // unmount per Christian's spec.
  }, []);
  return <AnalysisPage />;
}

function ProtectedShell() {
  // SB2-CC-01 — RoleContextProvider sits inside RequireAuth so the JWT
  // is available when `useRoleMatrix` fires its first GET.
  return (
    <RoleContextProvider>
      <ProtectedCanvas />
    </RoleContextProvider>
  );
}

/**
 * WS-NAV / mobile_strategy §2.2 — the device gate sits ONCE here rather than
 * per route: `routeDevice.ts` is the table, so a new desktop-only page is a
 * one-line entry and can never be forgotten at a route definition. The hook
 * has to run inside RoleContextProvider (the chrome reads the role), hence
 * the extra component.
 */
function ProtectedCanvas() {
  // A pop-out widget window (SB6-ANA-POP-01, `?popout=1`) is a small window on
  // a DESKTOP, not a phone — it is routinely narrower than the `md` breakpoint.
  // Gating it would replace the popped widget with the "needs a desktop" card
  // on the machine that already is one, so the capability gate skips pop-outs.
  const isMobile = useIsMobileViewport() && !isPopoutWindow();
  return (
    <AppChrome>
      <DeviceGate isMobile={isMobile}>
        <Outlet />
      </DeviceGate>
    </AppChrome>
  );
}

function PublicShell() {
  return (
    <LoginChrome>
      <Outlet />
    </LoginChrome>
  );
}

function RootGate() {
  // Fires useTenantFromHost ONCE for the whole app + clears stale
  // cross-tenant sessions before any protected query mounts.
  return (
    <TenantGate>
      <Outlet />
    </TenantGate>
  );
}

// Story 050 added Activity / My tasks / Inbox to the primary nav and
// the four Settings leaves to the secondary nav as Coming-soon stubs.
// Account profile + settings appear in the user-card popover.
// Story 051 dropped the legacy `/data-management` Coming-soon route
// (the leaf disappears from the sidebar; no end-user destination
// justifies a stub) — see Open Question 1 in
// docs/stories/051-data-acquisitions-rename.md.
// 2026-05-12 — General / Domains / Integrations / Payouts were dropped
// from the Settings group; the matching Coming-soon stubs are removed
// here so the routes don't outlive the sidebar entries. Billing stays.
const COMING_SOON: {
  path: string;
  title: string;
  description?: string;
}[] = [
  { path: "/activity", title: "Activity" },
  { path: "/tasks", title: "My tasks" },
  { path: "/inbox", title: "Inbox" },
  { path: "/reports", title: "Reports" },
  { path: "/statistics", title: "Statistics" },
  // Story 054 — `/parks` ComingSoonPage removed. The Assets → Parks
  // sidebar leaf flips to "Sites" with route `/sites`. Legacy bookmarks
  // to `/parks` fall into the SPA `*` catch-all (AC2 asserts no
  // redirect, no Coming-soon copy).
  // Story 057 — `/turbines` Coming-soon stub removed; the route now
  // mounts <TurbinesPage/> below.
  { path: "/defect-catalogue", title: "Defect Catalogue" },
  { path: "/settings/billing", title: "Billing" },
  // WS-NAV — the Field Data workspace (doc §2 W1). The pages themselves are
  // the next epic (upload wizard, task list, standby tracking); the workspace
  // ships now so the IA is complete and the routes exist to link to.
  // DI-2-1 — `/field-data/upload` is no longer a stub; it mounts the intake
  // wizard (<IntakeUploadPage/>, lazy) below. DI-2-9 — `/field-data/intake`
  // is no longer a stub either (<IntakesPage/>, DI-2-15). DI-3-R2 —
  // `/field-data/tasks` neither (<TasksPage/>, "My tasks").
  // TT-02 — `/field-data/standby` is no longer a stub; it mounts
  // <MyTimeTrackingsPage/> below. Route stays `device: "all"` (routeDevice.ts
  // default): it is one of the two MVP mobile pages.
  // NOTE: the §2a "Notification rules" stub is NOT here — it lives inside the
  // tenant_admin `RequireRole` subtree below, like the rest of `/admin/*`.
  // Story 052 — /account/profile still placeholder (redirects to
  // settings#profile in the route definition below). /account/settings
  // is replaced with the real SettingsPage.
];

const router = createBrowserRouter([
  {
    element: <RootGate />,
    children: [
      // Public routes — no RequireAuth, dedicated LoginChrome.
      {
        element: <PublicShell />,
        children: [
          { path: "/login", element: <LoginPage /> },
          { path: "/auth/callback", element: <CallbackPage /> },
          // SB5-A — invited users land here after clicking the email link.
          // No RequireAuth gate: the user has no Zitadel session yet.
          { path: "/invite/:code", element: <AcceptInvitePage /> },
        ],
      },
      // Protected routes — RequireAuth (renders <Outlet/>), then ProtectedShell.
      {
        element: <RequireAuth />,
        children: [
          {
            element: <ProtectedShell />,
            children: [
              // Story 049 — `/` mounts the real Dashboard (was a
              // placeholder from Story 050; the chrome stays the same,
              // the page body is now the configurable widget canvas).
              { path: "/", element: <DashboardPage /> },
              // WS-NAV — the workspace overview. Always reachable (the
              // switcher's first entry); `?landing=1` marks a post-sign-in
              // arrival and applies the two landing rules.
              { path: "/home", element: <HomePage /> },
              // Story 051 — canonical route for the data-acquisitions
              // list page (renamed from /inspections). The legacy
              // /inspections URL redirects below so bookmarks survive.
              { path: "/data-acquisitions", element: <DataAcquisitionsPage /> },
              {
                path: "/inspections",
                element: <Navigate to="/data-acquisitions" replace />,
              },
              {
                path: "/inspections/*",
                element: <Navigate to="/data-acquisitions" replace />,
              },
              {
                path: "/analysis/:inspectionId",
                element: <AnalysisPageWithSidebarCollapse />,
              },
              // Story 054 — Sites management (Assets → Sites leaf).
              { path: "/sites", element: <SitesPage /> },
              // Story 057 — Turbines management (Assets → Turbines leaf).
              { path: "/turbines", element: <TurbinesPage /> },
              // Story 059 — Turbine Models management (Assets →
              // Turbine Models leaf, between Turbines and Components).
              { path: "/turbine-models", element: <TurbineModelsPage /> },
              // Story 061 — Components management (Assets → Components
              // leaf, between Turbine Models and Component Models).
              { path: "/components", element: <ComponentsPage /> },
              // Story 062 — Component Models management (Assets →
              // Component Models leaf, below Components).
              { path: "/component-models", element: <ComponentModelsPage /> },
              // SB2-W-16 — Project Management → Projects (net-new page).
              { path: "/projects", element: <ProjectsPage /> },
              // SB5-B.1 — Project Management → Contracts list page.
              { path: "/contracts", element: <ContractsPage /> },
              // SB5-B.2 — Project Management → Contract Positions list page.
              {
                path: "/contract-positions",
                element: <ContractPositionsPage />,
              },
              // SB6-II-01 — Project Management → Inspection Instructions.
              {
                path: "/inspection-instructions",
                element: <InspectionInstructionsPage />,
              },
              // PM-4 — Create Project full page (manual park/turbine attach).
              // MUST sit BEFORE the `/:projectId` param route below, else
              // `create` would be captured as a projectId and shadow this page.
              { path: "/projects/create", element: <CreateProjectPage /> },
              // TNCY-6 — Project detail page: the cross-tenant access-grant
              // panel + party pickers live here (owner tenant_admin only;
              // the BE is the security boundary). The pencil on the Projects
              // list now deep-links here instead of toasting "coming soon".
              {
                path: "/projects/:projectId",
                element: <ProjectDetailPage />,
              },
              // TT-03 — back-office review of a project's standby, and the
              // commercial config that prices it. Both are role-gated INSIDE
              // the page (the BE is the security boundary either way), so a
              // non-PM gets an explanation rather than a 404.
              {
                path: "/projects/:projectId/time-trackings",
                element: <TimeTrackingReviewPage />,
              },
              {
                path: "/projects/:projectId/settings",
                element: <ProjectSettingsPage />,
              },
              // Story 053 — Tenant user management. The /admin/* subtree
              // is role-gated to tenant_admin. Non-admins land on
              // ForbiddenPage (AC3). RequireRole wraps the subtree
              // routes; the parent /admin (no role gate) is a redirect
              // so the spec's `goto('/admin')` lands at /admin/users.
              {
                path: "/admin",
                element: <Navigate to="/admin/users" replace />,
              },
              {
                path: "/admin/forbidden",
                element: <ForbiddenPage />,
              },
              {
                element: (
                  // `role` here is the RequireRole prop, not the HTML
                  // ARIA role attribute, so jsx-a11y/aria-role is
                  // disabled at the attribute site.
                  <RequireRole
                    // eslint-disable-next-line jsx-a11y/aria-role
                    role="tenant_admin"
                    fallback={<ForbiddenPage />}
                  />
                ),
                children: [
                  { path: "/admin/users", element: <UsersListPage /> },
                  // Story 054a — `/admin/users/audit-log` mirrors the
                  // canonical `/admin/audit-log` route so the audit log
                  // can be linked from the Users surface. Acceptance
                  // spec 054a §AC6 expects the page at this path.
                  {
                    path: "/admin/users/audit-log",
                    element: <AuditLogPage />,
                  },
                  {
                    path: "/admin/users/:userId",
                    element: <UsersListPage />,
                  },
                  // TNCY-5 — the standalone Administration → Invitations
                  // leaf (pending USER invites) is retired; its pending-
                  // invites table folded into the Users page (UsersListPage
                  // "Pending invitations" section). The old route redirects
                  // to Users so existing bookmarks survive. The only
                  // invitation-flavoured Administration leaf now is Partner
                  // Companies (cross-tenant company links).
                  {
                    path: "/admin/invitations",
                    element: <Navigate to="/admin/users" replace />,
                  },
                  // TNCY-5 — cross-tenant company links (XT-D26).
                  {
                    path: "/admin/partner-companies",
                    element: <PartnerCompaniesPage />,
                  },
                  // FB-19 (final) — the "Max AI models per project" widget
                  // moved OUT of Administration to a plain "Limits" leaf
                  // under the Settings group. The page (TenantSettingsPage)
                  // now lives at `/settings/limits`, kept inside this SAME
                  // tenant_admin RequireRole subtree because it edits a
                  // tenant-wide setting via tenancy/v1 `/settings` (the BE
                  // re-asserts tenant_admin on PUT). Non-admins land on
                  // ForbiddenPage.
                  {
                    path: "/settings/limits",
                    element: <TenantSettingsPage />,
                  },
                  // FB-19 — old Administration route redirects to the new
                  // Settings/Limits route so existing bookmarks survive.
                  {
                    path: "/admin/ai-limits",
                    element: <Navigate to="/settings/limits" replace />,
                  },
                  { path: "/admin/audit-log", element: <AuditLogPage /> },
                  // WS-NAV (doc §2a) — per-customer / per-project automatic
                  // notifications; the first case is standby time on a Nordex
                  // project triggering an email. Stub for now, but role-gated
                  // from day one like the rest of `/admin/*`.
                  {
                    path: "/admin/notification-rules",
                    element: (
                      <ComingSoonPage
                        title="Notification rules"
                        description="Per-customer and per-project automatic notifications — e.g. standby time reported on a Nordex project triggers an email to the customer."
                      />
                    ),
                  },
                ],
              },
              // CM-5/CM-6 (CM-D1) — Administration → Contacts address book.
              // Read = every role except pilot (`contact.read`), so it
              // CANNOT live inside the tenant_admin subtree above. Gated by
              // an explicit non-pilot role set (RequireRole any-match);
              // pilot lands on ForbiddenPage. Write actions are guarded
              // client-side per-button via the role matrix, and the BE is
              // the security boundary on every mutation.
              {
                element: (
                  <RequireRole
                    // eslint-disable-next-line jsx-a11y/aria-role
                    role={[
                      "platform_admin",
                      "tenant_admin",
                      "project_manager",
                      "back_office",
                      "expert_analyst",
                      "pre_screener",
                    ]}
                    fallback={<ForbiddenPage />}
                  />
                ),
                children: [
                  { path: "/admin/contacts", element: <ContactsListPage /> },
                  {
                    path: "/admin/contacts/:contactId",
                    element: <ContactDetailPage />,
                  },
                ],
              },
              // SB2-W-01 — cross-tenant Users page for platform staff.
              // Gated to platform_admin; non-platform callers land on
              // ForbiddenPage. The BE `PATCH /platform-admin/users/{id}/role`
              // is the ultimate gate (only platform_admin passes the
              // matrix cell for `user.role_change_any`).
              {
                element: (
                  <RequireRole
                    // eslint-disable-next-line jsx-a11y/aria-role
                    role="platform_admin"
                    fallback={<ForbiddenPage />}
                  />
                ),
                children: [
                  {
                    path: "/platform-admin/users",
                    element: <CrossTenantUserListPage />,
                  },
                  // SUP-ADMIN-UI-01 — cross-tenant bug-report triage queue.
                  // platform_admin only; the support service is the ultimate
                  // gate (403 permission_denied to anyone else).
                  {
                    path: "/platform-admin/bug-reports",
                    element: <BugReportTriagePage />,
                  },
                ],
              },
              // Story 052 — Account settings + Profile.
              { path: "/account/settings", element: <SettingsPage /> },
              {
                path: "/account/profile",
                element: <Navigate to="/account/settings#profile" replace />,
              },
              // DEFCAT P5 — per-user Defect Type Collections management.
              {
                path: "/account/defect-collections",
                element: <DefectCollectionsPage />,
              },
              // DEFCAT P6 — Defect Catalogue editor (list + editor).
              // 2026-06-03 (Christian) — moved OUT of the tenant_admin-
              // gated `/admin/*` subtree to `/defect-catalogues`. Per the
              // defect-catalogue role matrix (`.vibe/_DEFECTS/10` + `12`)
              // READ is open to any authenticated user; writes (edit-draft
              // = tenant_admin/expert_analyst, publish = tenant_admin) are
              // enforced server-side by the taxonomy admin service, so the
              // route itself carries no role gate.
              {
                path: "/defect-catalogues",
                element: <CatalogueListPage />,
              },
              {
                path: "/defect-catalogues/:catalogueId",
                element: <CatalogueEditorPage />,
              },
              // AIF-01 — AI Models management (list + detail). READ is open
              // to any authenticated user; writes (create/edit/delete +
              // import) are role-gated server-side and via FE write
              // affordances, so the route carries no role gate.
              {
                path: "/ai-models",
                element: <AiModelsListPage />,
              },
              {
                path: "/ai-models/:modelId",
                element: <AiModelDetailPage />,
              },
              // AIF-03 — Import AI Findings wizard. The page itself
              // role-redirects ineligible users (expert_analyst /
              // tenant_admin / platform_admin may import); the server is
              // the security boundary on analyze/import.
              // TT-02 — Field Data → "Standby & Time Tracking". One route,
              // two layouts (cards on a phone, table on a desk); the page
              // picks by viewport rather than the router picking by device.
              {
                path: "/field-data/standby",
                element: <MyTimeTrackingsPage />,
              },
              // DI-2-1 — Field Data → Upload: Drop + Scan of the intake
              // wizard. Desktop-only via routeDevice.ts; open to every role,
              // like the Field Data workspace itself.
              {
                path: "/field-data/upload",
                element: (
                  <Suspense fallback={null}>
                    <IntakeUploadPage />
                  </Suspense>
                ),
              },
              // DI-2-15 — Field Data → My intake (the data page).
              {
                path: "/field-data/intake",
                element: (
                  <Suspense fallback={null}>
                    <IntakesPage />
                  </Suspense>
                ),
              },
              // DI-3-D — Field Data → Intake history (the finished table).
              // Device rule as My intake (`routeDevice.ts`: all).
              {
                path: "/field-data/intake/history",
                element: (
                  <Suspense fallback={null}>
                    <IntakeHistoryPage />
                  </Suspense>
                ),
              },
              // DI-3-R2 — Field Data → My tasks (title "My tasks").
              {
                path: "/field-data/tasks",
                element: (
                  <Suspense fallback={null}>
                    <TasksPage />
                  </Suspense>
                ),
              },
              {
                path: "/ai-findings-import",
                element: <ImportAiFindingsPage />,
              },
              ...COMING_SOON.map((p) => ({
                path: p.path,
                element: (
                  <ComingSoonPage
                    title={p.title}
                    {...(p.description === undefined
                      ? {}
                      : { description: p.description })}
                  />
                ),
              })),
              { path: "*", element: <Navigate to="/" replace /> },
            ],
          },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
